import { create } from 'zustand'
import {
  guestEntryService,
  BookingFullError,
  EntryAlreadyRemovedError,
  MainGuestAlreadyDesignatedError,
} from '@/services/guestEntryService'
import { useGeneralFormStore } from './generalFormStore'
import { isPreviewMode } from '@/lib/previewMode'
import type { FormGuestEntry, CreateGuestEntryRequest } from '@/types/guest'

interface GuestCount {
  current: number
  limit: number
  remaining: number
}

interface GuestRegistrationState {
  guests: FormGuestEntry[]
  guestCount: GuestCount
  isLoading: boolean
  isCreating: boolean
  isDeleting: boolean
  isDesignating: boolean

  fetchGuests: (publicId: string, signal?: AbortSignal) => Promise<void>
  createEntry: (publicId: string, payload: CreateGuestEntryRequest) => Promise<FormGuestEntry>
  deleteEntry: (publicId: string, entryId: string) => Promise<void>
  designateMainGuest: (publicId: string, entryId: string) => Promise<void>
  getCount: (publicId: string) => Promise<GuestCount>
}

export const useGuestRegistrationStore = create<GuestRegistrationState>((set, get) => ({
  guests: [],
  guestCount: { current: 0, limit: 0, remaining: 0 },
  isLoading: true,
  isCreating: false,
  isDeleting: false,
  isDesignating: false,

  /**
   * Fetch guest entries from the backend. Server is the sole source of truth.
   * No local entry re-injection — all guests exist on the backend.
   */
  fetchGuests: async (publicId: string, signal?: AbortSignal) => {
    if (isPreviewMode()) return;
    try {
      const response = await guestEntryService.listEntries(publicId, signal)
      const serverEntries = response.entries || []

      set({
        guests: serverEntries,
        guestCount: {
          current: response.currentCount,
          limit: response.declaredLimit,
          remaining: response.spotsRemaining,
        },
        isLoading: false,
      })
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') return
      const errObj = err as { code?: string; message?: string; name?: string }
      if (errObj.code === 'ERR_CANCELED' || errObj.message === 'canceled' || errObj.name === 'CanceledError') return
      console.error('Error fetching guest entries', err)
      set({ isLoading: false })
    }
  },

  /**
   * Creates a guest entry on the backend immediately.
   * No temp IDs, no localStorage — backend returns real UUID.
   * Throws BookingFullError if no spots available.
   *
   * Story 20-2 Phase 1: also stashes the returning-guest profileMatch (if present)
   * into useGeneralFormStore IMMEDIATELY after the POST returns, while `created`
   * is still in scope. The subsequent fetchGuests refresh strips profileMatch
   * (Decision 23b — POST-only), so reading state.guests[i].profileMatch later
   * returns undefined.
   */
  createEntry: async (publicId: string, payload: CreateGuestEntryRequest) => {
    if (isPreviewMode()) {
      return {
        id: 'preview-entry',
        formId: publicId,
        isMainGuest: false,
        complianceStatus: 'STARTED',
      } as FormGuestEntry;
    }
    set({ isCreating: true })
    try {
      const created = await guestEntryService.createEntry(publicId, payload)

      // Capture profileMatch BEFORE the GET refresh wipes it.
      useGeneralFormStore.getState().setProfileMatch(created.id, created.profileMatch ?? null)

      // Refresh guest list from server to get accurate state
      await get().fetchGuests(publicId)

      return created
    } finally {
      set({ isCreating: false })
    }
  },

  /**
   * Deletes a guest entry from the backend.
   * Optimistic removal with rollback on error.
   */
  deleteEntry: async (publicId: string, entryId: string) => {
    if (isPreviewMode()) return;
    const { guests, guestCount } = get()
    const previousGuests = [...guests]
    const previousCount = { ...guestCount }

    // Optimistic removal
    set({
      isDeleting: true,
      guests: guests.filter(g => g.id !== entryId),
      guestCount: {
        current: guestCount.current - 1,
        limit: guestCount.limit,
        remaining: guestCount.remaining + 1,
      },
    })

    try {
      await guestEntryService.deleteEntry(publicId, entryId)
      // Refresh from server for accurate state
      await get().fetchGuests(publicId)
    } catch (err) {
      // Rollback optimistic update
      set({ guests: previousGuests, guestCount: previousCount })
      throw err
    } finally {
      set({ isDeleting: false })
    }
  },

  /**
   * Designates a guest as the primary contact (main guest).
   * Simple flag flip — no data reshuffling, no form re-initialization.
   */
  designateMainGuest: async (publicId: string, entryId: string) => {
    if (isPreviewMode()) return;
    set({ isDesignating: true })
    try {
      await guestEntryService.designateMainGuest(publicId, entryId)

      // Update local state immediately — just flip the flag
      set((state) => ({
        guests: state.guests.map(g => ({
          ...g,
          isMainGuest: g.id === entryId,
        })),
      }))

      // Update generalFormStore's guest designation flag
      useGeneralFormStore.getState().updateGuestDesignation(entryId)
    } finally {
      set({ isDesignating: false })
    }
  },

  /**
   * Pre-check availability before opening a new guest form.
   */
  getCount: async (publicId: string) => {
    if (isPreviewMode()) {
      const { guestCount } = get();
      return guestCount;
    }
    const response = await guestEntryService.getCount(publicId)
    const count = {
      current: response.currentCount,
      limit: response.declaredLimit,
      remaining: response.spotsRemaining,
    }
    set({ guestCount: count })
    return count
  },
}))

export { BookingFullError, EntryAlreadyRemovedError, MainGuestAlreadyDesignatedError }
